import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';
import { nextRequestToken, isStaleRequest, loadStart, loadSettle } from './shared';

export const createCondosUnitsSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // Condos Management API calls
  fetchCondos: async () => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const condos = await apiService.getCondos();
      set({ condos, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch condos');
    } finally {
      setLoading(false);
    }
  },

  createCondo: async (condoData) => {
    const { setLoading, setError, fetchCondos } = get();
    setLoading(true);
    try {
      await apiService.createCondo(condoData);
      await fetchCondos();
    } catch (error: any) {
      setError(error.message || 'Failed to create condo');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateCondo: async (id, condoData) => {
    const { setLoading, setError, fetchCondos } = get();
    setLoading(true);
    try {
      await apiService.updateCondo(id, condoData);
      await fetchCondos();
    } catch (error: any) {
      setError(error.message || 'Failed to update condo');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteCondo: async (id) => {
    const { setLoading, setError, fetchCondos } = get();
    setLoading(true);
    try {
      await apiService.deleteCondo(id);
      await fetchCondos();
    } catch (error: any) {
      setError(error.message || 'Failed to delete condo');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // Units Management
  fetchUnits: async (condoId?: string) => {
    const token = nextRequestToken('units');
    set(loadStart('units')); // 106.1 — mark THIS resource loading + clear its prior error.
    try {
      const units = await apiService.getUnits(condoId);
      // 94.4 — drop a stale response: a newer fetch (e.g. a condo switch)
      // started after us, so this data is for the wrong/previous condo. A stale
      // resolution must NOT flip the keyed flags (106.1).
      if (isStaleRequest(token)) return;
      set(state => ({ ...loadSettle('units', null)(state), units }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('units', error.message || 'Failed to fetch units'));
    }
  },

  createUnit: async (unitData) => {
    const { setLoading, setError, fetchUnits } = get();
    setLoading(true);
    try {
      await apiService.createUnit(unitData);
      await fetchUnits(unitData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create unit');
    } finally {
      setLoading(false);
    }
  },

  // Synchronous update (for local state updates)
  updateUnit: (id, updatedUnit) => {
    set((state) => ({
      units: state.units.map((unit) =>
        unit.id === id ? { ...unit, ...updatedUnit } : unit
      ),
    }));
  },

  // Async API update
  updateUnitApi: async (id, unitData) => {
    const { setLoading, setError, getUnitById, fetchUnits } = get();
    setLoading(true);
    try {
      await apiService.updateUnit(id, unitData);
      const unit = getUnitById(id);
      await fetchUnits(unit?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update unit');
    } finally {
      setLoading(false);
    }
  },

  // Synchronous delete (for local state updates)
  deleteUnit: (id) => {
    set((state) => ({
      units: state.units.filter((unit) => unit.id !== id),
    }));
  },

  // Async API delete
  deleteUnitApi: async (id) => {
    const { setLoading, setError, getUnitById, fetchUnits } = get();
    setLoading(true);
    try {
      const unit = getUnitById(id);
      await apiService.deleteUnit(id);
      await fetchUnits(unit?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete unit');
    } finally {
      setLoading(false);
    }
  },

  getUnitById: (id) =>
    get().units.find(unit => unit.id === id),

  getUnitsByCondo: (condoId) =>
    get().units.filter(unit => unit.condoId === condoId),

  getUnitsByStatus: (status, condoId) => {
    const { units } = get();
    const filteredUnits = condoId ? units.filter(u => u.condoId === condoId) : units;
    return filteredUnits.filter(unit => unit.status === status);
  },

  getAvailableUnits: (condoId) =>
    get().getUnitsByStatus('available', condoId),

  getOccupiedUnits: (condoId) =>
    get().getUnitsByStatus('occupied', condoId),

  getMaintenanceUnits: (condoId) =>
    get().getUnitsByStatus('maintenance', condoId),

  filterUnits: (filters, condoId) => {
    const { units } = get();
    let filteredUnits = condoId ? units.filter(u => u.condoId === condoId) : units;

    if (filters.status && filters.status.length > 0) {
      filteredUnits = filteredUnits.filter(u => filters.status?.includes(u.status));
    }

    if (filters.minFloor !== undefined) {
      filteredUnits = filteredUnits.filter(u => u.floor >= filters.minFloor!);
    }

    if (filters.maxFloor !== undefined) {
      filteredUnits = filteredUnits.filter(u => u.floor <= filters.maxFloor!);
    }

    if (filters.minArea !== undefined) {
      filteredUnits = filteredUnits.filter(u => u.area >= filters.minArea!);
    }

    if (filters.maxArea !== undefined) {
      filteredUnits = filteredUnits.filter(u => u.area <= filters.maxArea!);
    }

    if (filters.bedrooms !== undefined) {
      filteredUnits = filteredUnits.filter(u => u.bedrooms === filters.bedrooms);
    }

    if (filters.bathrooms !== undefined) {
      filteredUnits = filteredUnits.filter(u => u.bathrooms === filters.bathrooms);
    }

    if (filters.hasOwner !== undefined) {
      filteredUnits = filteredUnits.filter(u => filters.hasOwner ? !!u.ownerId : !u.ownerId);
    }

    return filteredUnits;
  },

  assignOwnerToUnit: async (unitId, ownerId) => {
    const { updateUnitApi, getUnitById } = get();
    const unit = getUnitById(unitId);

    if (unit && unit.status === 'available') {
      await updateUnitApi(unitId, {
        ownerId,
        status: 'occupied'
      });
    }
  },

  removeOwnerFromUnit: async (unitId) => {
    const { updateUnitApi } = get();
    await updateUnitApi(unitId, {
      ownerId: undefined,
      status: 'available'
    });
  },

  updateUnitStatus: async (unitId, status) => {
    const { updateUnitApi } = get();
    await updateUnitApi(unitId, { status });
  },

  // Dashboard Stats
  getDashboardStats: (condoId?: string) => {
    const { owners, fees, getCurrentQuarterReport, getUnitStats } = get();

    // Filter data by condo if specified
    const filteredOwners = condoId ? owners.filter(owner => owner.condoId === condoId) : owners;
    const filteredFees = condoId ? fees.filter(fee => fee.condoId === condoId) : fees;

    const report = getCurrentQuarterReport(condoId);
    const pendingFees = filteredFees.filter(fee => fee.status === 'PENDING');
    const overdueFees = filteredFees.filter(fee => fee.status === 'OVERDUE');

    const unitStats = getUnitStats(condoId);

    return {
      totalOwners: filteredOwners.length,
      totalRevenue: report.totalRevenue,
      totalExpenses: report.totalExpenses,
      netIncome: report.netIncome,
      pendingFees: pendingFees.length,
      overdueFees: overdueFees.length,
      feeCollectionRate: report.feeCollectionRate,
      occupancyRate: unitStats.occupancyRate,
      maintenanceRequests: 0,
      totalUnits: unitStats.totalUnits,
      occupiedUnits: unitStats.occupiedUnits,
      vacantUnits: unitStats.vacantUnits
    };
  },

  fetchDashboardStats: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const stats = await apiService.getDashboardStats(condoId);
      return stats;
    } catch (error: any) {
      setError(error.message || 'Failed to fetch dashboard stats');
      throw error;
    } finally {
      setLoading(false);
    }
  },
});
